// Copyright 2025, University of Maryland, All Rights Reserved
import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { HashRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, Outlet } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster, DebugIndicator } from '@umd-dit-ai-workspace/shared-ui'
import { AppHeader, NavigationItem, AdminPage } from '@umd-dit-ai-workspace/workspaces-component'
import AccessDenied from './components/AccessDenied'
import GetStartedPage from './pages/GetStartedPage'
import DocumentsPage from './pages/DocumentsPage'
import BulkComparisonResultsPage from './pages/BulkComparisonResultsPage'
import ConfigPage from './pages/ConfigPage'
import Jobs from './pages/Jobs'
import Costs from './pages/Costs'
// Import the direct content comparison page
import ComparisonPage from './pages/ComparisonPage'
import LearningObjectivesPage from './pages/LearningObjectivesPage'
import SetAnalysisPage from './pages/SetAnalysisPage'
import AdminRequestsPage from './pages/AdminRequestsPage'
// Keep old pages for reference but don't use in main routes
import AnalysisPage from './pages/AnalysisPage'
import { AuthProvider, useAuth } from '@umd-dit-ai-workspace/workspaces-component'
import { apiClient } from '@/api'
import { initializeWorkspacesApi } from '@umd-dit-ai-workspace/workspaces-component'
import { initializeJobsApi } from '@umd-dit-ai-workspace/jobs-component'
import { CASAuthProvider } from '@umd-dit-ai-workspace/cas-auth'
import { ReportIssueButton } from '@/components/ReportIssueButton'
import "@/styles/globals.css"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5000,
      refetchOnWindowFocus: false,
      placeholderData: (previousData: unknown) => previousData,
      throwOnError: (error: Error) => {
        return error?.message?.includes('401') || error?.message?.includes('403')
      }
    },
    mutations: {
      retry: 1,
      retryDelay: 1000
    }
  }
})

function PreserveQueryParamsRoute() {
  const location = useLocation()
  return <Navigate to={`/analysis${location.search}`} replace />
}

function Layout() {
  const { isAdmin, currentWorkspace } = useAuth()
  const navigate = useNavigate()
  const isWorkspaceAdmin = isAdmin || currentWorkspace?.role?.toLowerCase() === 'admin'
  const isCoordinator = currentWorkspace?.role?.toLowerCase() === 'coordinator'

  // Define navigation items specific to course-align
  const navigationItems: NavigationItem[] = [
    {
      path: '/documents',
      label: 'Documents'
    },
    {
      path: '/analysis',
      label: 'Analysis'
    },
    {
      path: '/comparison',
      label: 'Comparison',
      activeWhen: ['/bulk-comparison']
    },
    ...(isWorkspaceAdmin || isCoordinator ? [{
      path: '/learning-objectives',
      label: 'Learning Outcomes'
    }] : []),
    {
      path: '/set-analysis',
      label: 'Feedback Analysis'
    },
    ...(isWorkspaceAdmin ? [
      {
        path: '/config',
        label: 'Config',
      },
    ] : []),
    ...(isWorkspaceAdmin ? [
      {
        path: '/workspace-requests',
        label: 'Requests',
      },
    ] : []),
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <AppHeader
        appId="course-align"
        navigationItems={navigationItems}
        showDashboard={false}
        debugMode={import.meta.env.DEV || import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
        onRequestNewWorkspace={() => navigate('/request-workspace')}
      />

      <main className="py-6">
        <div className="mx-auto px-4">
          <Outlet />
        </div>
      </main>

      {/* Floating Report Issue Button - Bottom Right */}
      <div className="fixed bottom-6 right-6 z-50">
        <ReportIssueButton email="dit-ais@umd.edu" />
      </div>
    </div>
  );
}

// Routing component with authentication and workspace awareness
function WorkspaceAwareRouting() {
  const {
    isAuthenticated,
    isLoading,
    loadingWorkspaces,
    hasWorkspaceAccess,
  } = useAuth();

  // If loading auth or workspaces, show loading
  if (isLoading || loadingWorkspaces) {
    return (
      <div className="container mx-auto p-6 flex justify-center items-center h-screen">
        <div className="text-center">
          <p className="text-xl font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // If authenticated but no workspace access, show access denied
  if (!isAuthenticated) {
    return <AccessDenied/>;
  }

  // Authenticated but no workspace access → get started page
  if (!hasWorkspaceAccess) {
    return <GetStartedPage />
  }

  // Render the normal routes
  return (
    <Routes>
      <Route path="/" element={<PreserveQueryParamsRoute/>}/>

      <Route path="request-workspace" element={<GetStartedPage />} />

      {/* Main Application Layout */}
      <Route element={<Layout/>}>
        <Route path="documents" element={<DocumentsPage/>}/>
        <Route path="analysis" element={<AnalysisPage/>}/> {/* FIXED: Now shows individual document analysis */}
        <Route path="comparison" element={<ComparisonPage/>}/> {/* FIXED: Now uses direct content comparison */}
        <Route path="comparison-results/:comparisonId" element={<BulkComparisonResultsPage/>}/>
        <Route path="learning-objectives" element={<LearningObjectivesPage/>}/>
        <Route path="set-analysis" element={<SetAnalysisPage/>}/>
        <Route path="workspace-requests" element={<AdminRequestsPage/>}/>
        <Route path="config" element={<ConfigPage/>}/>
        <Route path="jobs" element={<Jobs/>}/>
        <Route path="costs" element={<Costs/>}/>
        <Route path="admin" element={
          <AdminPage
            app_id="course-align"
            appName="Course Alignment"
            debugMode={import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
          />
        }/>
        <Route path="/" element={<AnalysisPage/>}/>
      </Route>

      <Route path="*" element={<Navigate to="/" replace/>}/>
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Initialize Jobs API with our API client
    initializeJobsApi(apiClient.getAxiosInstance());

    // Initialize Workspaces API with our API client
    initializeWorkspacesApi(apiClient.getAxiosInstance());

    // Check for debug flag in URL on initial load
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === 'true') {
      console.log('[App] Debug mode activated via URL parameter');
      sessionStorage.setItem('debugModeEnabled', 'true');
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  console.log('[App] CAS Configuration:', {
    casLoginUrl: import.meta.env.VITE_CAS_LOGIN_URL,
    serviceUrl: import.meta.env.VITE_CAS_SERVICE_URL,
    apiGatewayUrl: import.meta.env.VITE_CAS_API_URL,
  });

  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <CASAuthProvider
          config={{
            casLoginUrl: import.meta.env.VITE_CAS_LOGIN_URL || 'https://login.umd.edu/cas/login',
            serviceUrl: import.meta.env.VITE_CAS_SERVICE_URL || window.location.origin,
            apiGatewayUrl: import.meta.env.VITE_CAS_API_URL || import.meta.env.VITE_API_URL + '/auth/cas/login',
            debug: import.meta.env.VITE_DEBUG_MODE === 'true',
          }}
          onAccessGranted={(authenticated: boolean, user) => {
            console.log('[App] CAS access granted:', { authenticated, user });
          }}
          onAccessDenied={() => {
            console.log('[App] CAS access denied');
          }}
        >
          <AuthProvider appName="course-align">
            <WorkspaceAwareRouting />
            <Toaster />
            <DebugIndicator />
          </AuthProvider>
        </CASAuthProvider>
      </QueryClientProvider>
    </Router>
  );
}

// Debug log to check environment variables
console.log('[DEBUG] Environment Variables:', {
  apiUrl: import.meta.env.VITE_API_URL,
  mode: import.meta.env.MODE,
  base: import.meta.env.BASE_URL
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);